import { useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  BanIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  InfoIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { MenuItem } from "simplydo/react/ui/menu";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";
import { Tab } from "simplydo/react/ui/tab";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { MultiLineParagraph } from "components/lib/UI";

import { AssessmentScoreProcessingWarning } from "./AssessmentScoreProcessingWarning";
import type {
  IdeaAssessmentInvitation,
  IdeaAssessmentReportChallenge,
  IdeaWithAssessments,
} from "./types";

type IdeaAssessmentReportProps = {
  idea: IdeaWithAssessments;
  challenge?: IdeaAssessmentReportChallenge;
};

const IdeaAssessmentReport = ({
  idea,
  challenge,
}: IdeaAssessmentReportProps) => {
  const user = useCurrentUser();
  const { t } = useTranslation();

  const [selectedAssessor, setSelectedAssessor] = useState("");

  const template = idea.ownerIdeaTemplate || challenge?.ownerIdeaTemplate;
  const assessmentRounds = useMemo(
    () => template?.assessments?.rounds || [],
    [template],
  );
  const [selectedAssessmentRoundId, setSelectedAssessmentRoundId] = useState(
    assessmentRounds?.at(0)?.id,
  );

  const invitationQuery = useQuery(
    initUseQuery(api.invitations.getForType, ["ideaAssessor", idea.challenge]),
  );

  // Pass array of idea ids and user ids, and remove the users as assessors from these ideas
  const removeAssessors = (
    ideaIds: string[],
    userIds: string[],
    assessmentRoundId: string,
  ) => {
    api.challenges.unassignAssessors(
      idea.challenge,
      ideaIds,
      userIds,
      assessmentRoundId,
      ({ ideas: assessorIdeas }) => {
        api.invalidateQueries(api.ideas.get, [idea._id]);
        api.setQueryData(api.challenges.getIdeas, [idea.challenge], (prev) => ({
          ...prev,
          ideas: (prev.ideas || []).map((idea) => {
            const updatedIdea = assessorIdeas.find((i) => i._id === idea._id);
            if (!updatedIdea) return idea;
            return { ...idea, assessments: updatedIdea.assessments };
          }),
        }));
        toast.success("Assessors updated");
      },
      (err) => toast.error(err.message),
    );
  };

  const removeAssessor = (
    assessment: NonNullable<OpenAPI.Schemas["Idea"]["assessments"]>[number],
  ) => {
    if (!selectedAssessmentRoundId) return;
    removeAssessors([idea._id], [assessment?.user], selectedAssessmentRoundId);
  };

  const removeInvite = (invitation: OpenAPI.Schemas["Invitation"]) => {
    util
      .confirm(
        t("ideas.invitations.remove.title"),
        t("ideas.invitations.remove.info_assessor"),
      )
      .then(() => {
        api.invitations.remove(
          invitation._id,
          () => {
            api.setQueryData(
              api.invitations.getForType,
              ["ideaAssessor", idea.challenge],
              (prev) => ({
                ...prev,
                invitations: prev.invitations.filter(
                  (i) => i._id !== invitation._id,
                ),
              }),
            );

            toast.success("Invite removed");
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  };

  const unsubmitAssessment = (
    assessment: NonNullable<OpenAPI.Schemas["Idea"]["assessments"]>[number],
  ) => {
    if (!selectedAssessmentRoundId) return;
    api.ideas.unsubmitAssessment(
      idea._id,
      selectedAssessmentRoundId,
      assessment.user,
      () => {
        toast.success("Assessment unsubmitted");
        api.invalidateQueries(api.ideas.get, [idea._id]);
        api.invalidateQueries(api.challenges.getIdeas, [idea.challenge]);
        api.invalidateQueries(api.challenges.getAssessmentDashboard, [
          idea.challenge,
        ]);
      },
      (err) => toast.error(err.message),
    );
  };

  const ideaId = idea._id;
  const invited = useMemo(
    () =>
      invitationQuery.data?.invitations
        .filter((i) => i.forIdeas?.includes(ideaId))
        .map((i) => ({
          ...i,
          profile: {
            firstName: i.inviteeUser?.profile?.firstName || i.invitee,
            created: `Invited ${formatDate(i.createdAt, "dd/MM/yy, HH:mm")}`,
          },
        })),
    [invitationQuery.data, ideaId],
  );

  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [idea.challenge], {
      isEnabled: !!idea.challenge,
    }),
  );
  const assessmentScoreProcessing =
    challengeQuery.data?.assessmentScoreProcessing === true;

  if (assessmentRounds.length === 0) {
    return null;
  }

  const scorePerRound = assessmentRounds.map((round) => {
    const details = idea.assessmentDetails?.assessmentRounds?.find(
      (r) => r.id === round.id,
    );

    return {
      ...round,
      averageSubmittedScore: details?.averageSubmittedScore,
      proportionalWeight: details?.proportionalWeight ?? 0,
      weightedScore: details?.weightedScore,
    };
  });
  const overallScore = idea.assessmentDetails?.overallScore ?? 0;

  return (
    <div
      data-testid="simplydo-idea-assessment-report"
      style={{ overflow: "auto", paddingRight: 8 }}
    >
      {assessmentScoreProcessing ? <AssessmentScoreProcessingWarning /> : null}
      <H5>
        Submitted assessment scores
        <Subheader>
          The average score is calculated by weighting the scores of the
          submitted assessments by the weight of the assessment round.
        </Subheader>
      </H5>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Assessment round</TableHeaderCell>
            <TableHeaderCell singleLine>
              Average score{" "}
              <Popup trigger={<Icon color="grey" icon={InfoIcon} />}>
                The average score is the combined score of all submitted
                assessments in this round, divided by the number of submitted
                assessments, shown to 2 decimal places.
              </Popup>
            </TableHeaderCell>
            <TableHeaderCell singleLine>
              Round weight{" "}
              <Popup
                trigger={<Icon color="grey" icon={InfoIcon} />}
              >{`This weight dictates how much of each round's score contributes to the total assessment score for this ${t("generic.idea")}. It may differ from the configured weight of the assessment round if there are assessment rounds with no submitted assessments, shown to 2 decimal places.`}</Popup>
            </TableHeaderCell>
            <TableHeaderCell singleLine>
              Weighted score{" "}
              <Popup trigger={<Icon color="grey" icon={InfoIcon} />}>
                {
                  "The weighted score is this round's contribution to the total, shown to 2 decimal places."
                }
              </Popup>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scorePerRound.map((round) => (
            <TableRow key={round.id}>
              <TableCell>{round.name}</TableCell>
              <TableCell>
                {round.averageSubmittedScore != null ? (
                  <Label color="violet" size="small">
                    {round.averageSubmittedScore}%
                  </Label>
                ) : (
                  <div>
                    N/A{" "}
                    <Popup trigger={<Icon color="grey" icon={InfoIcon} />}>
                      This assessment round is not included in the total score
                      because there are no submitted assessments yet.
                    </Popup>
                  </div>
                )}
              </TableCell>
              <TableCell collapsing>
                {round.averageSubmittedScore != null
                  ? `${round.proportionalWeight}%`
                  : ""}
              </TableCell>
              <TableCell collapsing>
                {round.averageSubmittedScore != null
                  ? `${round.weightedScore}%`
                  : ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableHeaderCell colSpan={3}>
              <b>Total combined round score </b>
              <Popup trigger={<Icon color="grey" icon={InfoIcon} />}>
                The total sum of the weighted scores, shown to 2 decimal places.
              </Popup>
            </TableHeaderCell>
            <TableHeaderCell>
              <Label color={overallScore > 0 ? "violet" : "grey"} size="small">
                {overallScore}%
              </Label>
            </TableHeaderCell>
          </TableRow>
        </TableFooter>
      </Table>

      <Divider />

      <Tab
        variant="underline"
        activeIndex={assessmentRounds.findIndex(
          (r) => r.id === selectedAssessmentRoundId,
        )}
        onTabChange={(_, data) => {
          const round = assessmentRounds[(data.activeIndex as number) || 0];
          if (!round) return;
          setSelectedAssessmentRoundId(round.id);
          setSelectedAssessor("");
        }}
        panes={assessmentRounds.map((round) => {
          const sentAt =
            idea?.ideaStatus?.assessmentFeedbackSent?.[round.id]?.sentAt;
          const roundAssessments =
            idea?.assessments?.filter(
              (a) => a.assessmentRoundId === round.id,
            ) || [];

          return {
            menuItem: (
              <MenuItem>
                {round.name}{" "}
                <Label size="small">{roundAssessments.length}</Label>
              </MenuItem>
            ),
            render: () => (
              <div key={round.id}>
                {sentAt ? (
                  <div style={{ marginBottom: 12 }}>
                    <Message size="small">
                      <Icon icon={CircleCheckIcon} />
                      <MessageHeader>
                        Feedback last included in a status update at{" "}
                        {formatDate(sentAt, "dd/MM/yy, HH:mm")}
                      </MessageHeader>
                    </Message>
                  </div>
                ) : null}

                <Accordion
                  styled
                  fluid
                  style={{ margin: 2 }}
                  scrollToContentOnOpen
                >
                  {roundAssessments.map(
                    (
                      a: NonNullable<
                        IdeaWithAssessments["assessments"]
                      >[number],
                    ) => {
                      const isSelected = selectedAssessor === a.user;
                      const assessorUser = a.ownerUser;
                      return (
                        <>
                          <AccordionTitle
                            active={isSelected}
                            onClick={() =>
                              setSelectedAssessor(isSelected ? "" : a.user)
                            }
                            label={
                              <>
                                {a.conflictOfInterest?.hasConflictOfInterest ? (
                                  <Label size="small" color="red">
                                    <Icon icon={TriangleAlertIcon} />
                                    Conflict of interest
                                  </Label>
                                ) : null}
                                {!a.conflictOfInterest?.hasConflictOfInterest &&
                                  a.isSubmitted && (
                                    <Label size="small" color="violet">
                                      {a.lockedAssessmentScore ?? 0}%
                                    </Label>
                                  )}
                                {a.isSubmitted ? (
                                  <Label size="small" color="green">
                                    Submitted
                                  </Label>
                                ) : (
                                  <Label size="small" color="orange">
                                    Not yet submitted
                                  </Label>
                                )}
                              </>
                            }
                          >
                            {assessorUser ? (
                              <UserChip
                                user={assessorUser}
                                onClick={
                                  isSelected
                                    ? undefined
                                    : () =>
                                        setSelectedAssessor(assessorUser._id)
                                }
                              />
                            ) : (
                              <span>Unknown user</span>
                            )}
                          </AccordionTitle>
                          <AccordionContent active={isSelected}>
                            {a.conflictOfInterest?.hasConflictOfInterest &&
                            a.conflictOfInterest?.notes ? (
                              <Message color="red" size="small">
                                <MessageHeader>
                                  Conflict of interest note
                                </MessageHeader>
                                <MessageContent>
                                  {a.conflictOfInterest.notes}
                                </MessageContent>
                              </Message>
                            ) : (
                              template?.body
                                ?.filter(
                                  (section) =>
                                    section.type === "assessment" &&
                                    section.assessmentRoundId === round.id,
                                )
                                .map((section) =>
                                  section.fields.map((field) => {
                                    const fieldAssessment =
                                      a.assessment?.[field.id];

                                    return (
                                      <div key={field.id}>
                                        <H4 style={{ marginBottom: 0 }}>
                                          {field.title}{" "}
                                          {fieldAssessment &&
                                          fieldAssessment.score ? (
                                            <Label>
                                              Score:{" "}
                                              {fieldAssessment.score || 0}
                                            </Label>
                                          ) : null}
                                          {fieldAssessment &&
                                          fieldAssessment.value ? (
                                            <Label>
                                              {fieldAssessment.value}
                                            </Label>
                                          ) : null}
                                        </H4>
                                        {fieldAssessment ? (
                                          <MultiLineParagraph>
                                            {fieldAssessment.notes || (
                                              <i>No comments left.</i>
                                            )}
                                          </MultiLineParagraph>
                                        ) : (
                                          <i>Not completed by this assessor.</i>
                                        )}
                                        <Divider />
                                      </div>
                                    );
                                  }),
                                )
                            )}
                            {util.isEmpty(a.assessment) &&
                            !a.conflictOfInterest?.hasConflictOfInterest ? (
                              <Message
                                size="small"
                                style={{ marginBottom: 12 }}
                              >
                                This assessor has yet to begin their assessment.
                              </Message>
                            ) : null}
                            {util.canManageChallenge(
                              user,
                              idea.ownerChallenge,
                            ) ||
                            util.hasPermission(
                              user,
                              "challenge.editAssessments",
                              idea.challenge,
                            ) ? (
                              <div
                                style={{
                                  marginTop: 5,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  marginBottom: 5,
                                }}
                              >
                                <Button
                                  size="small"
                                  style={{ marginBottom: 10 }}
                                  onClick={() => removeAssessor(a)}
                                  variant="outline"
                                >
                                  <Icon icon={Trash2Icon} />
                                  Remove assessor
                                </Button>
                                {a.isSubmitted ? (
                                  <Button
                                    size="small"
                                    style={{ marginBottom: 10, marginLeft: 5 }}
                                    onClick={() => unsubmitAssessment(a)}
                                    variant="outline"
                                  >
                                    <Icon icon={BanIcon} />
                                    Unsubmit assessment
                                  </Button>
                                ) : null}
                              </div>
                            ) : null}
                          </AccordionContent>
                        </>
                      );
                    },
                  )}
                </Accordion>
              </div>
            ),
          };
        })}
      />
      {invited && invited.length > 0 ? (
        <>
          <Divider section />
          <H5 style={{ marginRight: 16 }}>Pending invitations</H5>
          <Table>
            <TableBody>
              {invited.map((a: IdeaAssessmentInvitation) => {
                const assignedRoundIds =
                  a.ideaDetails?.[idea._id ?? ""]?.assessmentRounds || [];
                const assignedRounds = assignedRoundIds.map(
                  (id: string) =>
                    assessmentRounds.find((r) => r.id === id)?.name ||
                    "Unknown round",
                );

                return (
                  <TableRow key={a._id}>
                    <TableCell>
                      {a.inviteeUser?.profile?.firstName || a.invitee}
                    </TableCell>
                    <TableCell>{`${assignedRounds.join(", ")}`}</TableCell>
                    <TableCell collapsing textAlign="right">
                      {util.canManageChallenge(user, idea.ownerChallenge) ||
                      util.hasPermission(
                        user,
                        "challenge.editAssessments",
                        idea.challenge,
                      ) ? (
                        <Button
                          size="small"
                          onClick={() => removeInvite(a)}
                          variant="outline"
                        >
                          <Icon icon={Trash2Icon} />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      ) : null}
    </div>
  );
};

export default IdeaAssessmentReport;
